import { Injectable, OnDestroy } from '@angular/core';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { EMPTY, Observable, of, Subscription, timer } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ApiService } from 'core/api/api.service';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AuthenticationService } from 'core/authentication/authentication.service';
import { TouchpointNotificationSnackbarComponent } from './touchpoint-notification-snackbar.component';

export interface TouchpointNotificationPayload {
  id?: string | null;
  startDate?: string | Date;
  endDate?: string | Date;
  customer?: string;
  subject?: string;
}

@Injectable({
  providedIn: 'root'
})
export class TouchpointNotificationService implements OnDestroy {
  private readonly pollingIntervalMs = 60 * 1000;
  private readonly pendingNotificationsApiUrl = '/api/touchpoint/upcomingNotifications';
  private readonly currentMinuteNotificationsApiUrl = '/api/touchpoint/notifications';
  private pollingSubscription?: Subscription;
  private activeSnackbarRef?: MatSnackBarRef<TouchpointNotificationSnackbarComponent>;

  constructor(
    private apiService: ApiService,
    private appInfoService: AppInfoService,
    private authenticationService: AuthenticationService,
    private snackBar: MatSnackBar,
    private router: Router,
  ) { }

  start(): void {
    if (this.pollingSubscription || !this.shouldPoll()) {
      return;
    }

    this.pollingSubscription = timer(0, this.pollingIntervalMs).pipe(
      switchMap(tick => this.fetchNotifications(tick === 0 ? this.pendingNotificationsApiUrl : this.currentMinuteNotificationsApiUrl)),
      catchError(() => EMPTY)
    ).subscribe(notifications => {
      if (notifications.length) {
        this.showNotifications(notifications);
      }
    });
  }

  stop(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = undefined;
    this.activeSnackbarRef?.dismiss();
    this.activeSnackbarRef = undefined;
  }

  openNotification(notification: TouchpointNotificationPayload): void {
    this.activeSnackbarRef?.dismiss();
    this.router.navigate(['/touchpoints'], {
      queryParams: notification.id ? { openTouchpointId: notification.id } : {},
    });
  }

  ngOnDestroy(): void {
    this.stop();
  }

  private shouldPoll(): boolean {
    return this.authenticationService.isAuthenticated && this.appInfoService.isAccountManager();
  }

  private fetchNotifications(url: string): Observable<TouchpointNotificationPayload[]> {
    if (!this.shouldPoll()) {
      return of([]);
    }

    return this.apiService.get<TouchpointNotificationPayload[]>(url).pipe(
      map(response => Array.isArray(response) ? response : []),
      catchError(() => of([]))
    );
  }

  private showNotifications(notifications: TouchpointNotificationPayload[]): void {
    const items = notifications
      .filter(notification => notification?.subject || notification?.startDate)
      .map(notification => ({ message: this.formatMessage(notification), notification }));

    if (!items.length) {
      return;
    }

    this.activeSnackbarRef?.dismiss();
    this.activeSnackbarRef = this.snackBar.openFromComponent(TouchpointNotificationSnackbarComponent, {
      duration: 0,
      verticalPosition: 'top',
      horizontalPosition: 'end',
      data: {
        items,
        actionLabel: 'Open',
      },
      panelClass: ['ds', 'info', 'touchpoint-notification-snackbar']
    });

    this.activeSnackbarRef.afterDismissed().subscribe(() => {
      if (this.activeSnackbarRef) {
        this.activeSnackbarRef = undefined;
      }
    });
  }

  private formatMessage(notification: TouchpointNotificationPayload): string {
    const timeLabel = this.formatTimeRange(notification.startDate, notification.endDate);
    const customer = notification.customer?.trim();

    return [timeLabel, customer].filter(Boolean).join(' / ')
      || (notification.subject ?? 'Upcoming touchpoint');
  }

  private formatTimeRange(startDate?: string | Date, endDate?: string | Date): string {
    const start = this.toValidDate(startDate);
    if (!start) {
      return '';
    }

    const end = this.toValidDate(endDate);
    if (end && end.getTime() !== start.getTime()) {
      return `${this.formatTime(start)} - ${this.formatTime(end)}`;
    }

    return this.formatTime(start);
  }

  private toValidDate(value?: string | Date): Date | null {
    if (!value) {
      return null;
    }
    const date = new Date(value);
    // Reject invalid dates and .NET DateTime.MinValue ("0001-01-01T00:00:00").
    return Number.isNaN(date.getTime()) || date.getFullYear() <= 1 ? null : date;
  }

  private formatTime(date: Date): string {
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
}
